import { useState, useEffect, useCallback, useMemo } from 'react';
import type { SyncCartRequest } from '@gearghost/api-schemas/cart';

const GUEST_CART_KEY = 'gearghost_guest_cart';

/**
 * Shape of a listing row stored in the guest (localStorage) cart. This is
 * intentionally lighter than the authenticated cart schema's CartListing —
 * guest carts don't have availability, replacement cost, or anything else
 * derived from the backend. On login, the guest cart is posted to the
 * cart/sync endpoint and the authoritative schema'd cart replaces it.
 */
export type GuestCartListing = {
  listingId: string;
  quantity: number;
  title: string;
  primaryImage: string | null;
  dailyRentalPrice: number;
  vendorId: string | null;
  vendorName: string;
};

/** Input shape for `addListing` — only `listingId` is required, the rest default. */
export type GuestCartListingInput = {
  listingId: string;
  quantity?: number;
  title?: string;
  primaryImage?: string | null;
  dailyRentalPrice?: number;
  vendorId?: string | null;
  vendorName?: string;
};

/** Vendor grouping derived on each render from the flat listings array. */
export type GuestVendorGroup = {
  vendor: { id: string; storeName: string };
  listings: GuestCartListing[];
};

/** The localStorage'd cart document — what gets JSON.parse'd on mount. */
type GuestCartState = {
  listings: GuestCartListing[];
  pickupDate: string | null;
  returnDate: string | null;
};

/**
 * Default empty cart structure
 */
const getEmptyCart = (): GuestCartState => ({
  listings: [],
  pickupDate: null,
  returnDate: null,
});

/**
 * Load cart from localStorage. Any parse failure or shape mismatch
 * degrades to an empty cart rather than throwing.
 */
const loadCartFromStorage = (): GuestCartState => {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    if (!stored) return getEmptyCart();

    const parsed = JSON.parse(stored);

    // Validate structure
    if (!parsed || typeof parsed !== 'object') return getEmptyCart();

    return {
      listings: Array.isArray(parsed.listings) ? parsed.listings : [],
      pickupDate: parsed.pickupDate || null,
      returnDate: parsed.returnDate || null,
    };
  } catch {
    console.warn('Failed to parse guest cart from localStorage');
    return getEmptyCart();
  }
};

/**
 * Save cart to localStorage
 */
const saveCartToStorage = (cart: GuestCartState): void => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
  } catch (err) {
    console.warn('Failed to save guest cart to localStorage:', err);
  }
};

/**
 * Hook for managing guest (unauthenticated) cart in localStorage.
 *
 * Mirrors the subset of cart operations that make sense without a
 * backend (add, update quantity, remove, set dates, clear). On login,
 * `useCartMergeOnLogin` takes the `getCartForSync()` payload and POSTs
 * it to the sync endpoint.
 */
const useGuestCart = () => {
  const [cart, setCart] = useState<GuestCartState>(() => loadCartFromStorage());
  const [isLoaded] = useState(true);

  // Save cart to localStorage on changes (after initial load)
  useEffect(() => {
    if (isLoaded) {
      saveCartToStorage(cart);
    }
  }, [cart, isLoaded]);

  /**
   * Add listing to cart
   * If listing exists, increments quantity
   */
  const addListing = useCallback((listing: GuestCartListingInput) => {
    if (!listing?.listingId) {
      console.warn('addListing requires listingId');
      return;
    }

    setCart((prev) => {
      const existingIndex = prev.listings.findIndex(
        (l) => l.listingId === listing.listingId
      );

      if (existingIndex >= 0) {
        // Increment quantity
        const newListings = [...prev.listings];
        const existing = newListings[existingIndex]!;
        newListings[existingIndex] = {
          ...existing,
          quantity: existing.quantity + (listing.quantity || 1),
        };
        return { ...prev, listings: newListings };
      }

      // Add new listing
      const newListing: GuestCartListing = {
        listingId: listing.listingId,
        quantity: listing.quantity || 1,
        // Store additional info for display (not sent to backend)
        title: listing.title || '',
        primaryImage: listing.primaryImage || null,
        dailyRentalPrice: listing.dailyRentalPrice || 0,
        vendorId: listing.vendorId || null,
        vendorName: listing.vendorName || '',
      };

      return { ...prev, listings: [...prev.listings, newListing] };
    });
  }, []);

  /**
   * Update listing quantity
   * If quantity is 0 or less, removes the listing
   */
  const updateQuantity = useCallback((listingId: string, quantity: number) => {
    setCart((prev) => {
      if (quantity <= 0) {
        return {
          ...prev,
          listings: prev.listings.filter((l) => l.listingId !== listingId),
        };
      }

      return {
        ...prev,
        listings: prev.listings.map((l) =>
          l.listingId === listingId ? { ...l, quantity } : l
        ),
      };
    });
  }, []);

  /**
   * Remove listing from cart
   */
  const removeListing = useCallback((listingId: string) => {
    setCart((prev) => ({
      ...prev,
      listings: prev.listings.filter((l) => l.listingId !== listingId),
    }));
  }, []);

  /**
   * Set pickup and return dates
   */
  const setDates = useCallback((pickupDate: string | null, returnDate: string | null) => {
    setCart((prev) => ({
      ...prev,
      pickupDate,
      returnDate,
    }));
  }, []);

  /**
   * Clear entire cart
   */
  const clearCart = useCallback(() => {
    setCart(getEmptyCart());
    localStorage.removeItem(GUEST_CART_KEY);
  }, []);

  /**
   * Cart payload for POST /api/user/cart/sync. Strips display-only
   * fields (title, primaryImage, etc.) the wire schema rejects, and
   * keeps only what SyncCartRequestSchema validates.
   */
  const getCartForSync = useCallback((): SyncCartRequest => {
    return {
      listings: cart.listings.map(({ listingId, quantity }) => ({
        listingId,
        quantity,
      })),
      pickupDate: cart.pickupDate,
      returnDate: cart.returnDate,
    };
  }, [cart]);

  // Computed values
  const listingCount = useMemo(
    () => cart.listings.length,
    [cart.listings]
  );

  const totalQuantity = useMemo(
    () => cart.listings.reduce((sum, listing) => sum + listing.quantity, 0),
    [cart.listings]
  );

  const isEmpty = useMemo(
    () => cart.listings.length === 0,
    [cart.listings]
  );

  const hasDates = useMemo(
    () => Boolean(cart.pickupDate && cart.returnDate),
    [cart.pickupDate, cart.returnDate]
  );

  // Group listings by vendor
  const vendors = useMemo<GuestVendorGroup[]>(() => {
    const vendorMap = new Map<string, GuestVendorGroup>();

    cart.listings.forEach((listing) => {
      const vendorId = listing.vendorId || 'unknown';
      if (!vendorMap.has(vendorId)) {
        vendorMap.set(vendorId, {
          vendor: {
            id: vendorId,
            storeName: listing.vendorName || 'Unknown Vendor',
          },
          listings: [],
        });
      }
      vendorMap.get(vendorId)!.listings.push(listing);
    });

    return Array.from(vendorMap.values());
  }, [cart.listings]);

  return {
    // State
    listings: cart.listings,
    pickupDate: cart.pickupDate,
    returnDate: cart.returnDate,
    isLoaded,

    // Computed
    listingCount,
    totalQuantity,
    isEmpty,
    hasDates,
    vendors,

    // Methods
    addListing,
    updateQuantity,
    removeListing,
    setDates,
    clearCart,
    getCartForSync,
  };
};

export default useGuestCart;
